import { PaginationPayload, TableConfig } from 'app/shared/pagination/models';
import {
  DEFAULT_TABLE_PREFERENCES,
  U21TableColumn,
  U21TableColumnTypesType,
  U21TablePreference,
  U21TableProps,
  U21TableState,
} from 'app/shared/u21-ui/components';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { useLocalStorage } from 'app/shared/hooks/useLocalStorage';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

export const createTableColumnConfig = <T extends object>(
  columns: TableConfig[],
  additionalConfig: Record<string, Partial<U21TableColumn<T>>> = {},
): U21TableColumn<T>[] => {
  return columns.reduce((acc, i) => {
    if (i.hidden) {
      return acc;
    }
    const { key, label, sortable = true, type, ...restColumnProps } = i;
    let additionalProps = additionalConfig[i.key];
    // for table columns with a column constant that also utilize DataSettingDataDisplay
    // e.g. entity.type
    if (additionalProps && restColumnProps.Cell) {
      // remove the `Cell` prop so DataSettingDataDisplay doesn't override custom rendering logic
      const { Cell, accessor, ...restColumnPropsWithoutCell } = restColumnProps;
      additionalProps = { ...restColumnPropsWithoutCell, ...additionalProps };
    } else {
      additionalProps = { ...restColumnProps, ...additionalProps };
    }
    return [
      ...acc,
      {
        accessor: key,
        id: key,
        disableSortBy: !sortable,
        Header: label,
        type: type as U21TableColumnTypesType,
        ...additionalProps,
      },
    ];
  }, []);
};

export const createPaginationPayload = (
  state: U21TableState,
): PaginationPayload => {
  const { page, pageSize, sortBy } = state;
  const { desc, id } = sortBy[0] ?? {};
  return {
    limit: pageSize,
    offset: page,
    sort_column: id,
    sort_direction: desc ? 'descending' : 'ascending',
  };
};

export const useTablePreferencesAndState = ({
  defaultTablePreferences = DEFAULT_TABLE_PREFERENCES,
  tablePreferencesLocalStorageKey,
}: {
  defaultTablePreferences?: U21TablePreference;
  tablePreferencesLocalStorageKey: LocalStorageKeys;
}): {
  setTableState: Dispatch<SetStateAction<U21TableState>>;
  tableProps: Required<
    Pick<
      U21TableProps<any>,
      | 'defaultColumnWidths'
      | 'defaultSortBy'
      | 'defaultPageSize'
      | 'onPreferenceChange'
      | 'onStateChange'
    >
  >;
  tableState: U21TableState;
} => {
  const [tablePreferences, setTablePreferences] =
    useLocalStorage<U21TablePreference>(
      tablePreferencesLocalStorageKey,
      defaultTablePreferences,
    );

  const [tableState, setTableState] = useState<U21TableState>({
    page: 1,
    pageSize: tablePreferences.pageSize,
    sortBy: tablePreferences.sortBy,
  });

  const tableProps = useMemo(
    () => ({
      defaultColumnWidths: tablePreferences.columnWidths,
      defaultPageSize: tablePreferences.pageSize,
      defaultSortBy: tablePreferences.sortBy,
      onPreferenceChange: setTablePreferences,
      onStateChange: setTableState,
    }),
    [setTablePreferences, tablePreferences],
  );

  return { tableProps, setTableState, tableState };
};
